import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './CaseDocument.module.scss';
import DocumentUploadPage from 'src/components/DocumentUploadPage/DocumentUploadPage';
import DocumentChecklist from 'src/components/DocumentChecklist/DocumentChecklist';
import { IChecklistItem } from 'src/shared/types/Documents';
import { useGetCaseChecklist } from 'src/shared/queries/useGetCaseChecklist';
import AllCasePassports from 'src/components/AllCasePassports/AllCasePassports';
import classNames from 'classnames';
import AdditionalDocuments from 'src/components/AdditionalDocuments/AdditionalDocuments';
import caretRightIcon from 'src/assets/icons/caret-right.svg';
import caretDownIcon from 'src/assets/icons/caret-down.svg';
import { AsyncDataHandler, Tooltip } from '@ds';
import { TooltipPosition, TooltipType } from '@ds/elements/Tooltip/Tooltip.types';
import InfoCircleIcon from 'src/assets/icons/info-circle.svg';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import ChecklistProgressBar from '@ds/elements/ChecklistProgressBar/ChecklistProgressBar';
import { isChecklistItemComplete } from 'src/utils/services/documents';
import { useGetAdditionalDocuments } from 'src/shared/queries/useGetAdditionalDocuments';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { useGetMembers } from 'src/shared/queries/useGetMembers';
import { ICaseInfo } from 'src/shared/types/Case';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useGetDocumentTypes } from 'src/shared/queries/useGetDocumentTypes';
import { PassportClickInterceptor } from 'src/components/PassportClickInterceptor/PassportClickInterceptor';

interface CaseDocumentProps {
  caseDetails: ICaseInfo;
}

const CaseDocument: React.FC<CaseDocumentProps> = ({ caseDetails }) => {
  const { data: beneMembers } = useGetMembers(caseDetails?.beneId || 0);
  const { data: user } = useGetUser();
  const { data: dataDocumentTypes } = useGetDocumentTypes();
  const caseId = caseDetails.caseId;
  // const [members, setMembers] = useState<IBeneMember[]>([]);
  const passportsOpen = useRef<Boolean>(false);
  const additionalOpen = useRef<Boolean>(false);
  const [checklistOpenMobile, setChecklistOpenMobile] = useState(false);
  const screenSize = useScreenSize();
  const documentTypes = dataDocumentTypes || [];

  const {
    data: additionalDocs,
    isLoading: isLoadingAdditionalDocs,
    refetch: refetchAdditionalDocs,
  } = useGetAdditionalDocuments(caseId ?? 0);

  const {
    data: items,
    isLoading: checklistItemsLoading,
    refetch: refetchChecklistItems,
  } = useGetCaseChecklist(caseId || 0);

  useEffect(() => {
    if (!items) return;

    const filtered = items.filter((item) => item.checklistDefinition?.checklistType === 'BENEFICIARY');

    if (filtered.length > 0) {
      setChecklistDocuments(filtered);
      passportsOpen.current = false;
      additionalOpen.current = false;
    } else {
      passportsOpen.current = true;
      setChecklistDocuments([]);
    }
  }, [items]);

  const [checklistDocuments, setChecklistDocuments] = useState<IChecklistItem[]>([]);
  const [currentCount, setCurrentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentItem, setCurrentItem] = useState<IChecklistItem>({} as IChecklistItem);

  const handlePassportClick = () => {
    setCurrentItem({} as IChecklistItem);
    passportsOpen.current = true;
    additionalOpen.current = false;
  };

  const handleDocumentsClick = () => {
    setCurrentItem({} as IChecklistItem);
    additionalOpen.current = true;
    passportsOpen.current = false;
  };

  const refreshChecklist = async () => {
    try {
      await refetchChecklistItems();
    } catch (error) {
      console.error('Error refreshing checklist:');
    }
  };

  useEffect(() => {
    if (!currentItem || !currentItem.id) {
      if (checklistDocuments?.length === 0) return;
      setCurrentItem(checklistDocuments?.[0] || ({} as IChecklistItem));
    } else {
      const updatedItem = checklistDocuments.find((item) => item.id === currentItem.id);
      setCurrentItem(updatedItem || ({} as IChecklistItem));
    }

    if (checklistDocuments?.length) {
      const visibleItems = checklistDocuments.filter((item) => item.isShowing);
      setTotalCount(visibleItems.length);
      setCurrentCount(visibleItems.filter((item) => isChecklistItemComplete(item)).length);
    } else {
      setTotalCount(0);
      setCurrentCount(0);
    }
  }, [checklistDocuments]);

  const toggleChecklistOpenMobile = () => {
    setChecklistOpenMobile((prev) => !prev);
  };

  const DOCTYPE_PASSPORT = 'Passport';
  const DOCTYPE_OTHER = 'Other';

  const passportDocTypeId = useMemo(() => {
    return documentTypes.find((dt) => dt.docTypeName === DOCTYPE_PASSPORT)?.docTypeId;
  }, [documentTypes]);

  const otherDocTypeId = useMemo(() => {
    return documentTypes.find((dt) => dt.docTypeName === DOCTYPE_OTHER)?.docTypeId;
  }, [documentTypes]);

  const isPassportType = currentItem?.checklistDefinition?.documentTypeId === passportDocTypeId;

  const hasPassportItem = useMemo(() => {
    return checklistDocuments.some((item) => item?.checklistDefinition?.documentTypeId === passportDocTypeId);
  }, [checklistDocuments, passportDocTypeId]);

  return (
    <BeneDocAdditionalContext.Provider
      value={{
        passportDocTypeId: passportDocTypeId,
        beneMembers: beneMembers || [],
        otherDocTypeId: otherDocTypeId,
        beneId: caseDetails?.beneId,
        companyId: caseDetails?.companyId,
        caseId: caseDetails?.caseId,
        userId: user?.userId,
        hasPassportItem: hasPassportItem,
        caseInfo: caseDetails,
      }}
    >
      <div className={styles['case-documents']}>
        <div className={styles['checklist-progress-container']}>
          {checklistDocuments.length > 0 && (
            <div className={styles['mobile-checklist-accordion']} onClick={toggleChecklistOpenMobile}>
              <p>Document Checklist</p>
              <img src={checklistOpenMobile ? caretDownIcon : caretRightIcon} />
            </div>
          )}
          <AsyncDataHandler isLoading={checklistItemsLoading} loader={{ width: '250px', height: '600px' }}>
            <PassportClickInterceptor>
              {(!screenSize.isMobile || checklistOpenMobile === true) && checklistDocuments.length > 0 && (
                <div>
                  <p className={styles['progress-bar-header']}>Document Checklist</p>
                  <ChecklistProgressBar
                    className="progress-bar"
                    total={totalCount}
                    progress={currentCount}
                    fill={'#fdd106'}
                  />
                  <DocumentChecklist
                    items={checklistDocuments}
                    currentItem={currentItem}
                    onChecklistItemChange={(item) => {
                      setCurrentItem((prev) => (prev?.id === item?.id ? prev : item));
                      passportsOpen.current = false;
                      additionalOpen.current = false;
                    }}
                  />
                </div>
              )}
              <div
                className={classNames(
                  styles['passports-and-additional'],
                  passportsOpen.current === true ? styles['active'] : ''
                )}
                onClick={handlePassportClick}
              >
                <p>All Case Passports</p>
              </div>
              <div
                className={classNames(
                  styles['passports-and-additional'],
                  additionalOpen.current === true ? styles['active'] : ''
                )}
                onClick={handleDocumentsClick}
              >
                <p>Additional Documents</p>
                <img
                  src={InfoCircleIcon}
                  className={classNames(styles['info-icon'], 'additional-docs-info-icon')}
                  onClick={(e) => e.stopPropagation()}
                />
                <Tooltip
                  target=".additional-docs-info-icon"
                  content="Upload any additional immigration documents requested by the immigration team"
                  position={TooltipPosition.Right}
                  type={TooltipType.Info}
                  className={styles['additional-docs-tooltip']}
                />
              </div>
            </PassportClickInterceptor>
          </AsyncDataHandler>
        </div>
        {additionalOpen.current ? (
          <AdditionalDocuments
            additionalDocs={additionalDocs || []}
            isLoadingAdditionalDocs={isLoadingAdditionalDocs}
            refetch={refetchAdditionalDocs}
          />
        ) : passportsOpen.current ? (
          <AllCasePassports />
        ) : (
          <AsyncDataHandler isLoading={checklistItemsLoading} loader={{ width: '800px', height: '900px' }}>
            <DocumentUploadPage
              checklistItem={currentItem}
              onRefresh={refreshChecklist}
              onRefreshAdditionalDocs={refetchAdditionalDocs}
              isPassport={isPassportType}
              setCurrentItem={setCurrentItem}
            />
          </AsyncDataHandler>
        )}
      </div>
    </BeneDocAdditionalContext.Provider>
  );
};

export default CaseDocument;
